import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useApi } from "@/hooks/useApi";
import { useTranslation } from "@/hooks/useTranslation";
import { Loader2Icon } from "lucide-react";
import { useState } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Separator } from "../ui/separator";
import { useToast, useToastError } from "../ui/use-toast";

export default function DialogTeamKnowledgeWebsite({
  open,
  onClose,
  onAdd,
}: {
  open: boolean;
  onClose: () => void;
  onAdd: (url: string) => void;
}) {
  const toastError = useToastError();
  const api = useApi();
  const { t } = useTranslation();
  const { toast } = useToast();
  const [url, setUrl] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!url) {
      toastError();
      return;
    }

    try {
      setIsLoading(true);
      await api.post("/my/team/knowledge-base/websites", {
        url,
      });
      toast({
        variant: "default",
        title: t("team.knowledge.dialog.website.toast.success.title"),
      });
      onAdd(url);
      onClose();
    } catch (error) {
      toastError();
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-[600px] w-full">
        <DialogHeader>
          <DialogTitle>{t("team.knowledge.dialog.website.title")}</DialogTitle>
        </DialogHeader>
        <form onSubmit={onSubmit}>
          <Separator className="my-4" />
          <div className="overflow-auto max-h-[70vh] px-3">
            <div className="space-y-5">
              <div className="grid w-full items-center gap-1.5">
                <Label htmlFor="title">
                  {t("team.knowledge.dialog.website.form.urlLabel")}
                </Label>
                <Input
                  type="url"
                  id="url"
                  placeholder={t(
                    "team.knowledge.dialog.website.form.urlPlaceholder"
                  )}
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  required
                />
              </div>
            </div>
          </div>
          <Separator className="my-4" />
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="ghost">
                {t("team.knowledge.dialog.website.buttons.cancel")}
              </Button>
            </DialogClose>
            {isLoading ? (
              <Button disabled>
                <Loader2Icon className="w-4 h-4 animate-spin" />
              </Button>
            ) : (
              <Button type="submit">
                {t("team.knowledge.dialog.website.buttons.add")}
              </Button>
            )}
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
